import React, { useState } from 'react';
import axios from '../config/axiosConfig';
import './LoginModal.css';
import { FaUser, FaLock, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../contexts/TranslationContext';

const LoginModal = ({ onClose, onLogin, onSwitchToRegister }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Passwordless flow states
  const [emailChecked, setEmailChecked] = useState(false);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  // Step 1: Check email — determine if employer (passwordless) or staff (password)
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.email.includes('@')) {
      setError(t('invalidEmail') || 'Please enter a valid email address');
      return;
    }

    setCheckingEmail(true);

    try {
      const response = await axios.post(
        'https://hsn.urugowoc.org.rw/backend/api/check-user-type',
        { email: formData.email }
      );

      const userType = response.data.user_type;

      if (userType === 'employer') {
        // Employer → login directly with email only
        setRequiresPassword(false);
        setEmailChecked(true);
        await handleEmployerLogin();
      } else if (userType === 'manager' || userType === 'admin') {
        // Manager/Admin → show password field
        setRequiresPassword(true);
        setEmailChecked(true);
      } else {
        setError(t('unknownUserType') || 'Unknown user type. Please contact support.');
      }
    } catch (error) {
      console.error('Email check error:', error);
      if (error.response?.status === 404) {
        setError(t('noAccountFound') || 'No account found with this email. Please register first.');
      } else {
        setError(error.response?.data?.error || t('networkError') || 'Network error. Please try again.');
      }
    } finally {
      setCheckingEmail(false);
    }
  };

  // Step 2A: Employer passwordless login
  const handleEmployerLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        'https://hsn.urugowoc.org.rw/backend/api/login-employer',
        { email: formData.email }
      );

      onLogin(response.data.user, response.data.token);
      onClose();

      setTimeout(() => {
        try {
          navigate('/employer-dashboard', { replace: true });
        } catch (navError) {
          console.error('Navigation error:', navError);
          window.location.href = '/';
        }
      }, 100);
    } catch (error) {
      console.error('Employer login error:', error);
      setError(error.response?.data?.error || t('loginErrorGeneric') || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2B: Manager/Admin login with email + password
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('https://hsn.urugowoc.org.rw/backend/api/login', {
        email: formData.email,
        password: formData.password
      });

      onLogin(response.data.user, response.data.token);
      onClose();

      setTimeout(() => {
        try {
          switch (response.data.user.user_type) {
            case 'admin':
              navigate('/admin-dashboard', { replace: true });
              break;
            case 'manager':
              navigate('/manager-dashboard', { replace: true });
              break;
            case 'employer':
              navigate('/employer-dashboard', { replace: true });
              break;
            default:
              navigate('/', { replace: true });
              break;
          }
        } catch (navError) {
          console.error('Navigation error:', navError);
          window.location.href = '/';
        }
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      if (error.response) {
        setError(error.response.data?.error || t('loginErrorGeneric') || 'Login failed. Please check your credentials.');
      } else if (error.request) {
        setError(t('networkError') || 'Network error. Please check your connection.');
      } else {
        setError(t('loginErrorGeneric') || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset to email step
  const handleBackToEmail = () => {
    setEmailChecked(false);
    setRequiresPassword(false);
    setFormData(prev => ({ ...prev, password: '' }));
    setError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-modal-overlay">
      <div className="login-modal-content">
        <button className="login-close-modal" onClick={onClose} title={t('close') || 'Close'}>
          <FaTimes />
        </button>

        <div className="login-header">
          <h2>{t('loginWelcome') || 'Welcome Back'}</h2>
          <p>
            {!emailChecked
              ? (t('loginEmailOnlyHint') || 'Enter your email to continue')
              : requiresPassword
                ? (t('loginStaffHint') || 'Staff login — please enter your password')
                : (t('loggingIn') || 'Logging in...')}
          </p>
        </div>

        {error && (
          <div className="login-alert login-alert-error">
            {error}
          </div>
        )}

        {/* STEP 1: Email only */}
        {!emailChecked && (
          <form className="login-form" onSubmit={handleCheckEmail}>
            <div className="login-form-group login-with-icon">
              <FaUser className="login-form-icon" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('emailPlaceholder') || 'Enter your email'}
                required
                disabled={checkingEmail}
                className="login-input"
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="login-btn login-btn-primary"
              disabled={checkingEmail}
            >
              {checkingEmail
                ? (t('checking') || 'Checking...')
                : (t('continue') || 'Continue')}
            </button>
          </form>
        )}

        {/* STEP 2B: Manager/Admin password */}
        {emailChecked && requiresPassword && (
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-form-group login-with-icon login-readonly-email">
              <FaUser className="login-form-icon" />
              <input
                type="email"
                value={formData.email}
                readOnly
                className="login-input"
              />
              <button
                type="button"
                className="login-change-email-btn"
                onClick={handleBackToEmail}
                disabled={loading}
              >
                {t('change') || 'Change'}
              </button>
            </div>

            <div className="login-form-group login-with-icon">
              <FaLock className="login-form-icon" />
              <div className="login-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('passwordPlaceholder') || 'Enter your password'}
                  required
                  disabled={loading}
                  className="login-input"
                  autoFocus
                />
                <button
                  type="button"
                  className="login-password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div className="login-remember-forgot">
              <div className="login-remember-me">
                <input
                  type="checkbox"
                  id="login-remember"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <label htmlFor="login-remember">{t('rememberMe') || 'Remember me'}</label>
              </div>
            </div>

            <button
              type="submit"
              className="login-btn login-btn-primary"
              disabled={loading}
            >
              {loading ? (t('signingIn') || 'Signing in...') : (t('signIn') || 'Sign In')}
            </button>
          </form>
        )}

        {/* STEP 2A: Employer loading state */}
        {emailChecked && !requiresPassword && loading && (
          <div className="login-employer-loading">
            <div className="spinner"></div>
            <p>{t('loggingInEmployer') || 'Logging you in...'}</p>
          </div>
        )}

        {!emailChecked && (
          <div className="login-register-link">
            {t('noAccount') || "Don't have an account?"}{' '}
            <a href="#" onClick={(e) => {
              e.preventDefault();
              onClose();
              onSwitchToRegister();
            }}>
              {t('signUp') || 'Sign up here'}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;